import type { PlatformAPI, StorageMode, Mode, Theme, Language } from './types'
import { getUserSettings, updateUserSettings } from '../supabase/queries'
import { getCurrentUserId } from '../supabase/client'
import { createWebReminderDelivery } from './webReminders'

/**
 * Web Platform Adapter
 * Implementation for web browser
 */
export class WebAdapter implements PlatformAPI {
  platform = 'web' as const
  private listeners = new Map<string, Set<(...args: any[]) => any>>()
  /** Erinnerungen im Tab — nur solange einer offen ist. */
  reminders = createWebReminderDelivery()

  // ==================== STORAGE MODE ====================

  async getStorageMode(): Promise<StorageMode> {
    // Web is always cloud-sync
    return 'cloud-sync'
  }

  async setStorageMode(_mode: StorageMode): Promise<void> {
    // No-op for web - always cloud-sync
    console.warn('[Platform] Storage mode cannot be changed in web version')
  }

  // ==================== MODE ====================

  async getMode(): Promise<Mode> {
    // Try to get from Supabase first
    const userId = await getCurrentUserId()
    if (userId) {
      const settings = await getUserSettings(userId)
      if (settings?.theme) return settings.theme
    }

    // Fallback to localStorage
    const storedMode = localStorage.getItem('mode')
    return (storedMode as Mode) || 'light'  // Default to light for new users
  }

  async setMode(mode: Mode): Promise<void> {
    // Save to localStorage for immediate access
    localStorage.setItem('mode', mode)

    // Save to Supabase
    const userId = await getCurrentUserId()
    if (userId) {
      await updateUserSettings(userId, { theme: mode })
    }

    // Trigger listeners
    this.triggerListeners('mode-changed', mode)

  }

  onModeChanged(callback: (mode: Mode) => void): () => void {
    // Add to listeners
    if (!this.listeners.has('mode-changed')) {
      this.listeners.set('mode-changed', new Set())
    }
    this.listeners.get('mode-changed')!.add(callback)

    // Listen to storage events for cross-tab sync
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'mode' && e.newValue) {
        callback(e.newValue as Mode)
      }
    }

    window.addEventListener('storage', handleStorageChange)

    // Return cleanup function
    return () => {
      this.listeners.get('mode-changed')?.delete(callback)
      window.removeEventListener('storage', handleStorageChange)
    }
  }

  // ==================== THEME ====================

  async getTheme(): Promise<Theme> {
    const stored = localStorage.getItem('theme')
    return (stored as Theme) || 'standard'
  }

  async setTheme(theme: Theme): Promise<void> {
    localStorage.setItem('theme', theme)
    this.triggerListeners('theme-changed', theme)
  }

  onThemeChanged(callback: (theme: Theme) => void): () => void {
    if (!this.listeners.has('theme-changed')) {
      this.listeners.set('theme-changed', new Set())
    }
    this.listeners.get('theme-changed')!.add(callback)

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'theme' && e.newValue) {
        callback(e.newValue as Theme)
      }
    }
    window.addEventListener('storage', handleStorageChange)

    return () => {
      this.listeners.get('theme-changed')?.delete(callback)
      window.removeEventListener('storage', handleStorageChange)
    }
  }

  // ==================== LANGUAGE ====================

  async getLanguage(): Promise<Language | null> {
    // Try to get from Supabase first
    const userId = await getCurrentUserId()
    if (userId) {
      const settings = await getUserSettings(userId)
      if (settings?.language) return settings.language
    }

    // Fallback to localStorage
    const storedLanguage = localStorage.getItem('language')
    return (storedLanguage as Language) || null
  }

  async setLanguage(language: Language): Promise<void> {
    // Save to localStorage for immediate access
    localStorage.setItem('language', language)

    // Save to Supabase
    const userId = await getCurrentUserId()
    if (userId) {
      await updateUserSettings(userId, { language })
    }

  }

  // ==================== WINDOW CONTROLS ====================

  minimizeWindow(): void {
  }

  maximizeWindow(): void {
  }

  closeWindow(): void {
    // Try to close the window (may be blocked by browser)
    window.close()
  }

  isMaximizable(): boolean {
    return false
  }

  isMinimizable(): boolean {
    return false
  }

  // ==================== EXTERNAL LINKS ====================

  async openExternal(url: string): Promise<void> {
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  // ==================== SETTINGS ====================

  async getSetting(key: string): Promise<any> {
    // In web, settings are stored in Supabase via user_settings table
    // For custom settings, use localStorage
    const stored = localStorage.getItem(`setting_${key}`)
    if (stored) {
      try {
        return JSON.parse(stored)
      } catch {
        return stored
      }
    }
    return null
  }

  async setSetting(key: string, value: any): Promise<void> {
    // Store in localStorage
    localStorage.setItem(`setting_${key}`, JSON.stringify(value))

    // Could also sync to Supabase custom settings table if needed
  }

  async deleteSetting(key: string): Promise<void> {
    localStorage.removeItem(`setting_${key}`)
  }

  // ==================== DISPLAY MODE (not available in Web) ====================

  async getDisplayMode(): Promise<string> {
    console.warn('[Platform] Display mode not available in web')
    return 'window'
  }

  async setDisplayMode(_mode: string): Promise<void> {
    console.warn('[Platform] Display mode not available in web')
  }

  // ==================== PRIVATE HELPERS ====================

  private triggerListeners(event: string, ...args: any[]): void {
    const listeners = this.listeners.get(event)
    if (listeners) {
      listeners.forEach((callback) => callback(...args))
    }
  }
}
